import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Sidebar from "./Sidebar";
import MobileTabBar from "./MobileTabBar";
import MobilHlava from "./MobilHlava";
import Hledani from "./Hledani";
import { UpozorneniProvider } from "./Zvonek";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";

/** Neověřený e-mail (vlastní registrace): bez ověření se nepropojí firmy v přepínači. */
function OverEmail() {
  const [stav, setStav] = useState("");
  const poslat = async () => {
    setStav("posila");
    try { await api.post("/api/auth/overeni/znovu"); setStav("poslano"); }
    catch (e) { setStav(apiError(e.response?.data?.detail) || "Nepovedlo se."); }
  };
  return (
    <div className="mb-5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 rounded-card bg-[#fff6e5] text-[#7a5300] px-4 py-3" data-testid="over-email">
      <p className="t-meta flex-1">
        <b className="font-700">Ověř svůj e-mail</b> — odkaz jsme ti poslali po registraci. Bez ověření nepřepneš mezi firmami.
      </p>
      {stav === "poslano" ? <span className="t-meta font-700">Odkaz je na cestě.</span> : (
        <button type="button" onClick={poslat} disabled={stav === "posila"} data-testid="over-email-znovu"
          className="tap md:min-h-0 self-start sm:self-auto h-9 px-4 rounded-full bg-white t-meta font-700 text-ink shadow-[inset_0_0_0_1px_#f0d9a8]">
          {stav && stav !== "posila" ? stav : "Poslat odkaz znovu"}
        </button>
      )}
    </div>
  );
}

/** Zvonek (upozornění, otevření úkolu z e-mailu) je společný pro celé rozložení. */
export default function AppLayout({ children }) {
  return <UpozorneniProvider><Rozlozeni>{children}</Rozlozeni></UpozorneniProvider>;
}

function Rozlozeni({ children }) {
  // Hledání z levého panelu na počítači (na telefonu ho otevírá horní lišta).
  const [hledani, setHledani] = useState(false);
  useEffect(() => {
    const zkratka = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setHledani(true); }
    };
    document.addEventListener("keydown", zkratka);
    return () => document.removeEventListener("keydown", zkratka);
  }, []);
  // Sbalení levého panelu na počítači (jen ikony) — pamatuje se v prohlížeči.
  const [sbaleno, setSbaleno] = useState(() => {
    try { return localStorage.getItem("pp-panel-sbaleny") === "1"; } catch { return false; }
  });
  const prepniPanel = () => setSbaleno((v) => {
    try { localStorage.setItem("pp-panel-sbaleny", v ? "0" : "1"); } catch { /* bez paměti */ }
    return !v;
  });
  const { user, company } = useAuth();
  const isSub = (user?.roles || []).includes("SUBDODAVATEL") && !user?.superadmin;

  return (
    <div className="min-h-screen bg-surface">
      {/* Desktop full sidebar */}
      <div className="hidden lg:block fixed inset-y-0 left-0 z-30">
        <Sidebar collapsed={sbaleno} onHledat={() => setHledani(true)} zvonek />
        {/* Přepínač sbalení — kulaté tlačítko na hraně panelu */}
        <button type="button" onClick={prepniPanel} data-testid="sidebar-collapse-btn"
          aria-label={sbaleno ? "Rozbalit panel" : "Sbalit panel"} title={sbaleno ? "Rozbalit panel" : "Sbalit panel"}
          className="absolute -right-3 top-[22px] h-6 w-6 rounded-full bg-white border border-line-strong text-sec hover:text-ink shadow-card flex items-center justify-center">
          {sbaleno ? <ChevronRight size={14} strokeWidth={2} /> : <ChevronLeft size={14} strokeWidth={2} />}
        </button>
      </div>

      {/* Tablet icon rail */}
      <div className="hidden md:block lg:hidden fixed inset-y-0 left-0 z-30">
        <Sidebar collapsed />
      </div>

      <div className={`md:ml-[68px] ${sbaleno ? "lg:ml-[68px]" : "lg:ml-[236px]"} lg:transition-[margin] lg:duration-200 motion-reduce:transition-none`}>
        {/* Telefon a tablet: skleněné ostrůvky nahoře (16A) — menu modulů, hledání, zvonek, skener */}
        <MobilHlava />

        {/* Obsah jede pod lištami: nahoře místo pro ostrůvky, dole pro plovoucí lištu. */}
        <main className="px-4 md:px-8 pt-[calc(env(safe-area-inset-top)+72px)] lg:pt-6 pb-[calc(env(safe-area-inset-bottom)+104px)] lg:pb-10">
          <div className="mx-auto max-w-content">
            {isSub && (
              <div className="mb-5 flex items-start gap-3 rounded-card bg-ink text-white/90 px-4 py-3" data-testid="subdodavatel-banner">
                <span className="mt-1.5 h-2 w-2 rounded-full bg-accent shrink-0" />
                <p className="text-[12.5px] leading-relaxed">
                  Jsi tu jako subdodavatel firmy <strong className="text-white font-700">{(company?.name || "").replace(/\.$/, "")}</strong>. Vidíš jen klienty, ke kterým ti dali přístup.
                </p>
              </div>
            )}
            {user && user.email_verified === false && <OverEmail />}
            {children}
          </div>
        </main>
      </div>

      <div aria-hidden="true" className="lg:hidden fixed bottom-0 inset-x-0 md:left-[68px] z-[35] h-[calc(env(safe-area-inset-bottom)+96px)] pointer-events-none zavoj-dole" />
      <MobileTabBar />
      {hledani && <Hledani onClose={() => setHledani(false)} />}
    </div>
  );
}
